import { SearchField, ToggleButton, ToggleButtonGroup } from "@heroui/react"
import type { SelfOrderMenu } from "@ramoo/contracts"
import { tamu } from "@ramoo/i18n/tamu"
import { bisaDipesan, GridKatalog, KartuProduk } from "@ramoo/self-order-ui"
import { useMemo, useState } from "react"
import { cocokCari, kartuDari, type Produk, produkRekomendasi } from "./katalog"

const SEMUA = "_semua_kategori"
const teks = tamu()

/**
 * Katalog tamu: pencarian lokal + chip kategori satu baris yang bisa digeser + card grid. Kartu
 * memakai primitive bersama `@ramoo/self-order-ui` supaya sama dengan pratinjau owner. App yang
 * memutuskan quick-add vs sheet lewat `onPilih`.
 *
 * Chip kategori dulu membungkus ke dua baris dan mendorong produk pertama turun; mockup Modul 12
 * memilih satu baris geser, sama seperti v1.
 */
export function KatalogTamu({
  menu,
  tampilkanKategori,
  onPilih,
  jumlahDi,
}: {
  menu: SelfOrderMenu
  tampilkanKategori: boolean
  onPilih: (p: Produk) => void
  /** Jumlah tiap produk di keranjang (semua variannya dijumlah), untuk angka di tombol kartu. */
  jumlahDi: ReadonlyMap<string, number>
}) {
  const [cari, setCari] = useState("")
  const [kategoriAktif, setKategoriAktif] = useState<string | null>(null)
  const produkById = useMemo(() => new Map(menu.products.map((p) => [p.id, p])), [menu.products])

  const seksi = useMemo(() => {
    if (!tampilkanKategori)
      return [{ id: "_semua", nama: null as string | null, produk: menu.products }]
    const perKategori = menu.categories
      .map((c) => ({
        id: c.id,
        nama: c.name as string | null,
        produk: menu.products.filter((p) => p.categoryId === c.id),
      }))
      .filter((s) => s.produk.length)
    const lain = menu.products.filter(
      (p) => !p.categoryId || !menu.categories.some((c) => c.id === p.categoryId),
    )
    return lain.length
      ? [...perKategori, { id: "_lain", nama: teks.lainnya as string | null, produk: lain }]
      : perKategori
  }, [menu, tampilkanKategori])

  /* Rekomendasi pemilik tampil paling atas, dan produknya tetap juga di kategorinya sendiri.
     Memilih satu kategori berarti tamu mencari sesuatu yang spesifik, jadi seksinya diam. */
  const rekomendasi = kategoriAktif ? [] : produkRekomendasi(menu).filter((p) => cocokCari(p, cari))
  const seksiTampil = [
    ...(rekomendasi.length
      ? [{ id: "_rekomendasi", nama: teks.rekomendasi as string | null, produk: rekomendasi }]
      : []),
    ...seksi
      .filter((s) => !kategoriAktif || s.id === kategoriAktif)
      .map((s) => ({ ...s, produk: s.produk.filter((p) => cocokCari(p, cari)) }))
      .filter((s) => s.produk.length),
  ]

  return (
    <>
      <SearchField fullWidth aria-label={teks.cariAria} value={cari} onChange={setCari}>
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder={teks.cariMenu} />
          <SearchField.ClearButton aria-label={teks.hapusCari} />
        </SearchField.Group>
      </SearchField>
      {tampilkanKategori && seksi.length > 1 ? (
        <ToggleButtonGroup
          isDetached
          aria-label={teks.kategoriAria}
          className="-mx-4 w-[calc(100%+--spacing(8))] flex-nowrap justify-start gap-2 overflow-x-auto px-4 [scrollbar-width:none] [&>*]:shrink-0 [&>*]:rounded-full"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[kategoriAktif ?? SEMUA]}
          onSelectionChange={(kunci) => {
            const [pilih] = [...kunci]
            setKategoriAktif(!pilih || pilih === SEMUA ? null : String(pilih))
          }}
        >
          <ToggleButton id={SEMUA} size="sm">
            {teks.semua}
          </ToggleButton>
          {seksi.map((s) => (
            <ToggleButton key={s.id} id={s.id} size="sm">
              {s.nama ?? teks.lainnya}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : null}
      {seksiTampil.length ? (
        seksiTampil.map((s) => (
          <section key={s.id} className="flex flex-col gap-2">
            {s.nama ? <h2 className="text-base leading-6 font-semibold">{s.nama}</h2> : null}
            <GridKatalog>
              {s.produk.map((p) => (
                <KartuProduk
                  key={p.id}
                  data={kartuDari(p)}
                  unavailable={!bisaDipesan(p)}
                  unavailableLabel={teks.habis}
                  jumlah={jumlahDi.get(p.id) ?? 0}
                  labelTombol={
                    kartuDari(p).hasOptions ? teks.pilihProduk(p.name) : teks.tambahProduk(p.name)
                  }
                  onPilih={(id) => {
                    const pr = produkById.get(id)
                    if (pr) onPilih(pr)
                  }}
                />
              ))}
            </GridKatalog>
          </section>
        ))
      ) : (
        /* Keadaan kosong: komposisi teks (padanan `empty-state` Pro). Tidak ada aksi yang masuk
           akal di sini selain mengubah kata cari, dan kotak carinya tepat di atasnya. */
        <p className="px-4 py-8 text-center text-sm text-muted">
          {cari.trim() ? teks.kosongCari(cari.trim()) : teks.kosongMenu}
        </p>
      )}
    </>
  )
}
